import React, { useState, useRef, useEffect } from "react";
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Alert,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { useAuth } from "@/contexts/AuthContext";
import { api } from '@/api/client';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);


function mapLineData(items: any[], valueKey: string) {
  const labels = items.map((i) => i.date);
  const data = items.map((i) => Number(i[valueKey] ?? 0));
  return { labels, data };
}

function mapGatewaySeries(rawItems: any) {
  if (!rawItems) return { labels: [], series: [] };

  const items = (Array.isArray(rawItems) ? rawItems : [rawItems])
    .filter((item) => item && typeof item === 'object');

  if (items.length === 0) return { labels: [], series: [] };

  const hasGatewaysProp = items.some((item) => item.gateways && typeof item.gateways === 'object');

  if (hasGatewaysProp) {
    // Merge gateway keys across all items with `gateways` property
    const labels = items.map((item, idx) => item.date ?? `Entry ${idx + 1}`);
    const gatewayKeySet = new Set<string>();

    items.forEach((item) => {
      Object.keys(item.gateways || {}).forEach((key) => gatewayKeySet.add(key));
    });

    const gatewayKeys = Array.from(gatewayKeySet);
    const series = gatewayKeys.map((key) => ({
      label: key,
      data: items.map((item) => Number(item.gateways?.[key] ?? 0)),
    }));

    return { labels, series };
  }

  // Fallback: treat each item as a flat object of gateway -> value (optionally with date field)
  const gatewayKeySet = new Set<string>();
  items.forEach((item) => {
    Object.entries(item).forEach(([key, value]) => {
      if (key === 'date') return;
      if (typeof value === 'number' || typeof value === 'string') {
        gatewayKeySet.add(key);
      }
    });
  });

  const gatewayKeys = Array.from(gatewayKeySet);
  if (gatewayKeys.length === 0) return { labels: [], series: [] };

  let labels = items.map((item, idx) => item.date ?? `Entry ${idx + 1}`);
  if (labels.every((label) => !label)) {
    labels = items.map((_, idx) => `Entry ${idx + 1}`);
  }

  const series = gatewayKeys.map((key) => ({
    label: key,
    data: items.map((item) => Number(item?.[key] ?? 0)),
  }));

  return { labels, series };
}


type SeriesItem = {
  data: number[];
  label?: string;
  color?: string;
  [key: string]: unknown;
};

const Sparkline: React.FC<{
  data: number[];
  color?: string;
  height?: number;
}> = ({ data, color = '#2196F3', height = 60 }) => {
  const chartData = {
    labels: data.map((_, i) => i),
    datasets: [
      {
        data: data,
        borderColor: color,
        backgroundColor: color + '30',
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 0,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: {
      x: { display: false },
      y: { display: false },
    },
  };

  return (
    <div style={{ height, width: '100%' }}>
      <Line data={chartData} options={options} />
    </div>
  );
};


// Helper function to convert hex color to rgba with opacity
function hexToRgba(hex: string, opacity: number = 0.8): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const ResponsiveChart: React.FC<{
  dataLabels: string[];
  dataSeries: SeriesItem[];
  chartType: 'line' | 'bar';
  height?: number;
}> = ({ dataLabels, dataSeries, chartType, height = 300 }) => {
  const defaultColors = ['#E74C3C', '#2196F3', '#dfdbe0ff', '#2ECC71', '#F39C12', '#95A5A6', '#D35400', '#3498DB', '#E67E22', '#beabc5ff'];

  const chartData = {
    labels: dataLabels,
    datasets: dataSeries.map((series, index) => {
      const color = series.color || defaultColors[index % defaultColors.length];

      // For bar charts, add backgroundColor with some transparency for better visual appeal
      const backgroundColor = chartType === 'bar'
        ? hexToRgba(color, 0.85) // 85% opacity for attractive filled bars
        : undefined;

      return {
        label: series.label || `Dataset ${index + 1}`,
        data: series.data,
        backgroundColor: backgroundColor || color,
        borderColor: color,
        borderWidth: chartType === 'bar' ? 2 : 3,
        borderRadius: chartType === 'bar' ? 8 : 0,
        borderSkipped: false,
        tension: 0.4,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: color,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      };
    })
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: '500' as const,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        //bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function (context: any) {
            const value = context.parsed.y;
            return value === 0 ? null : `${context.dataset.label}: ${value.toLocaleString()}`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: chartType === 'line',
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          font: {
            size: 11,
          },
          stepSize: 1,
          precision: 0,
          callback: function (value: any) {
            return Number.isInteger(value) && value > 0 ? value.toLocaleString() : '';
          }
        },
      },
    },
  };

  return (
    <div style={{ height, width: '100%' }}>
      {chartType === 'line' ? (
        <Line data={chartData} options={options} />
      ) : (
        <Bar data={chartData} options={options} />
      )}
    </div>
  );
};


const DashboardPage: React.FC = () => {
  const { user, isAuthenticated, loading } = useAuth();

  // Show loading while auth is being verified
  if (loading) {
    return <div>Loading...</div>;
  }

  // If not authenticated, let ProtectedRoute handle redirect
  if (!isAuthenticated) {
    return null;
  }

  // State for chart data
  const [chartData, setChartData] = React.useState<any>(null);

  // Function to load chart details
  const loadChatsDetails = async (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
    try {
      const start = startDate.format('YYYY-MM-DD');
      const end = endDate.format('YYYY-MM-DD');
      const url = `/merchant/v1/dashboard-stats/?start_date=${start}&end_date=${end}`;
      const response = await api.get(url);
      setChartData(response.data || response);
    } catch (error) {
      console.error('Error loading chart details:', error);
      setChartData(null);
    }
  };

  // Validation function
  const validateDateRange = (start: dayjs.Dayjs, end: dayjs.Dayjs): string | null => {
    const today = dayjs().startOf('day');
    const MAX_DAYS = 7;

    if (!start || !end) {
      return 'Please select both start and end dates';
    }

    if (start.isAfter(end)) {
      return 'Start date cannot be after end date';
    }

    if (end.isAfter(today)) {
      return 'End date cannot be in the future';
    }

    // Check if date range exceeds maximum allowed days (7 days)
    const daysDifference = end.diff(start, 'day') + 1; // +1 to include both start and end dates
    if (daysDifference > MAX_DAYS) {
      return `Date range cannot exceed ${MAX_DAYS} days`;
    }

    return null;
  };



  return (
    <>
      <style>
        {`
          .MuiDateRangeCalendar-root div {
            color: #fff !important;
            z-index: 0 !important;
          }
          .css-7qgwht-MuiPaper-root {
            background-color: transparent;
            box-shadow: none;
            border: none;
          } 

        `}
      </style>
      {/* <Container
        maxWidth="xl"
        sx={{
          mt: { xs: 2, sm: 4 },
          mb: { xs: 2, sm: 4 },
          px: { xs: 2, sm: 3 },
        }}
      > */}
      {/* 🔹 Header Section */}

      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        mb={3}
        gap={{ xs: 2, sm: 0 }}
      >
        {/* 👤 User Info */}
        <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: "1.75rem", sm: "2rem", md: "2.125rem" },
              fontWeight: 600,
            }}
          >
            {(() => {
              const now = new Date();
              const hour = now.getHours();
              const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
              const dateStr = now.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              });
              return (
                <>

                  {user && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle1" fontWeight="medium" sx={{
                        fontSize: "32px",
                        lineHeight: "40px",
                        fontWeight: "bold",
                      }}>
                        {greeting.charAt(0).toUpperCase() + greeting.slice(1)}, {user?.name || user?.user_name || "Guest"}!
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{
                    margin: "0px",
                    fontWeight: "400",
                    fontSize: "1rem",
                    lineHeight: "1.5",
                    color: "rgb(108, 111, 117)",
                    paddingTop: "10px",
                  }}>
                    Today is {dateStr}.
                  </Typography >
                </>
              );
            })()}

          </Typography>


        </Box>


        {/* 📅 Date Range Picker with Weekday/Week Selection */}
        {(() => {
          const [open, setOpen] = React.useState(false);
          const [validationError, setValidationError] = React.useState<string | null>(null);

          // Default to last 7 days
          const today = dayjs().startOf('day');
          const [endDate, setEndDate] = React.useState(today);
          const [startDate, setStartDate] = React.useState(today.subtract(6, 'day'));

          // Temporary dates for dialog (before applying)
          const [tempStartDate, setTempStartDate] = React.useState(startDate);
          const [tempEndDate, setTempEndDate] = React.useState(endDate);

          // Load chart data on component mount and when dates change
          React.useEffect(() => {
            loadChatsDetails(startDate, endDate);
          }, []);

          // Show selected range in input
          const formatRange = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
            if (!start || !end) return '';
            return `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
          };

          // Get last day of current week (Sunday = 0, so Saturday = 6)
          const getLastDayOfWeek = (date: dayjs.Dayjs) => {
            const dayOfWeek = date.day(); // 0 = Sunday, 6 = Saturday
            const daysUntilSaturday = 6 - dayOfWeek;
            return date.add(daysUntilSaturday, 'day');
          };

          // Get start of current week (Sunday)
          const getStartOfWeek = (date: dayjs.Dayjs) => {
            return date.startOf('week'); // dayjs uses Sunday as start of week by default
          };

          // Get end of current week (Saturday)
          const getEndOfWeek = (date: dayjs.Dayjs) => {
            return date.endOf('week');
          };

          // Handle weekday selection (today to last day of week)
          const handleWeekdaySelection = () => {
            const lastDayOfWeek = getLastDayOfWeek(today);
            setTempStartDate(today);
            setTempEndDate(lastDayOfWeek.isAfter(today) ? today : lastDayOfWeek);
            setValidationError(null);
          };

          // Handle week selection (start of week to end of week, but not beyond today)
          const handleWeekSelection = () => {
            const weekStart = getStartOfWeek(today);
            const weekEnd = getEndOfWeek(today);
            setTempStartDate(weekStart);
            setTempEndDate(weekEnd.isAfter(today) ? today : weekEnd);
            setValidationError(null);
          };

          // Handle dialog open
          const handleOpen = () => {
            setTempStartDate(startDate);
            setTempEndDate(endDate);
            setValidationError(null);
            setOpen(true);
          };

          // Handle apply button
          const handleApply = () => {
            const error = validateDateRange(tempStartDate, tempEndDate);
            if (error) {
              setValidationError(error);
              return;
            }

            setStartDate(tempStartDate);
            setEndDate(tempEndDate);
            setValidationError(null);
            setOpen(false);
            loadChatsDetails(tempStartDate, tempEndDate);
          };

          // Validate on date change
          const handleStartDateChange = (newValue: dayjs.Dayjs | null) => {
            if (newValue) {
              setTempStartDate(newValue);
              const error = validateDateRange(newValue, tempEndDate);
              setValidationError(error);
            }
          };

          const handleEndDateChange = (newValue: dayjs.Dayjs | null) => {
            if (newValue) {
              setTempEndDate(newValue);
              const error = validateDateRange(tempStartDate, newValue);
              setValidationError(error);
            }
          };

          return (
            <>
              <TextField
                        size="small"
                variant="outlined"
                value={formatRange(startDate, endDate)}
                onClick={handleOpen}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarMonthIcon sx={{ cursor: 'pointer', color: 'action.active' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 180 }}
              />
              <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogContent sx={{ p: 0 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                        Select Date Range
                      </Typography>

                      {/* Two Calendars Side by Side */}
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' }, position: 'relative' }}>
                        <Box sx={{ flex: 1, minWidth: 280 }}>
                          <Box sx={{ position: 'relative' }}>
                            <DatePicker
                              value={tempStartDate}
                              onChange={handleStartDateChange}
                              maxDate={today}
                              open={true}
                              onClose={() => {}}
                              slotProps={{
                                textField: {
                                  sx: { 
                                    position: 'absolute',
                                    opacity: 0,
                                    pointerEvents: 'none',
                                    width: 0,
                                    height: 0,
                                  },
                                },
                                popper: {
                                  sx: {
                                    position: 'static !important',
                                    transform: 'none !important',
                                    zIndex: 'auto !important',
                                    '& .MuiPaper-root': {
                                      boxShadow: 'none',
                                      border: '1px solid #e0e0e0',
                                      borderRadius: 1,
                                    },
                                  },
                                  disablePortal: true,
                                  placement: 'bottom-start',
                                },
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 280 }}>
                          <Box sx={{ position: 'relative' }}>
                            <DatePicker
                              value={tempEndDate}
                              onChange={handleEndDateChange}
                              maxDate={tempStartDate.add(6, 'day').isAfter(today) ? today : tempStartDate.add(6, 'day')}
                              minDate={tempStartDate}
                              open={true}
                              onClose={() => {}}
                              slotProps={{
                                textField: {
                                  sx: { 
                                    position: 'absolute',
                                    opacity: 0,
                                    pointerEvents: 'none',
                                    width: 0,
                                    height: 0,
                                  },
                                },
                                popper: {
                                  sx: {
                                    position: 'static !important',
                                    transform: 'none !important',
                                    zIndex: 'auto !important',
                                    '& .MuiPaper-root': {
                                      boxShadow: 'none',
                                      border: '1px solid #e0e0e0',
                                      borderRadius: 1,
                                    },
                                  },
                                  disablePortal: true,
                                  placement: 'bottom-start',
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>

                      {/* Validation Error */}
                      {validationError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {validationError}
                        </Alert>
                      )}

                      {/* Selected Range Display */}
                      <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: 14, textAlign: 'center' }}>
                        Selected Range: {formatRange(tempStartDate, tempEndDate)}
                      </Typography>
                    </Box>
                  </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpen(false)}>Cancel</Button>
                  <Button
                    onClick={handleApply}
                    variant="contained"
                    disabled={validationError !== null}
                  >
                    Apply
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          );
        })()}

      </Box >


      {/* Dashboard Tabs */}
      <DashboardTabs chartData={chartData} />
    </>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `dashboard-tab-${index}`,
    'aria-controls': `dashboard-tabpanel-${index}`,
  };
}

const DashboardTabs: React.FC<{ chartData: any }> = ({ chartData }) => {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} aria-label="dashboard tabs">
          <Tab label="Overview" {...a11yProps(0)} />
          <Tab label="Volume" {...a11yProps(1)} />
          <Tab label="Conversion Rate" {...a11yProps(2)} />
          <Tab label="Risk" {...a11yProps(3)} />
        </Tabs>
      </Box>
      <TabPanel value={value} index={0}>
        {chartData && <OverviewTab sampleAnalytics={chartData} />}
      </TabPanel>
      <TabPanel value={value} index={1}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            textAlign: 'center',
            py: 4
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              opacity: 0.8
            }}
          >
            <Typography variant="h2" sx={{ color: 'primary.main' }}>📊</Typography>
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 1
            }}
          >
            Coming Soon
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: 400
            }}
          >
            Volume analytics and insights are being prepared for you. Stay tuned for detailed transaction volume data.
          </Typography>
        </Box>
        {/* {chartData && <VolumeTab sampleAnalytics={chartData} />} */}
      </TabPanel>
      <TabPanel value={value} index={2}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            textAlign: 'center',
            py: 4
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              opacity: 0.8
            }}
          >
            <Typography variant="h2" sx={{ color: 'primary.main' }}>📈</Typography>
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 1
            }}
          >
            Coming Soon
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: 400
            }}
          >
            Conversion rate analytics are in development. Track your payment success rates and optimization opportunities.
          </Typography>
        </Box>
        {/* {chartData && <ConversionRateTab sampleAnalytics={chartData} />} */}
      </TabPanel>
      <TabPanel value={value} index={3}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            textAlign: 'center',
            py: 4
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              opacity: 0.8
            }}
          >
            <Typography variant="h2" sx={{ color: 'primary.main' }}>🛡️</Typography>
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 1
            }}
          >
            Coming Soon
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: 400
            }}
          >
            Risk management dashboard is being built. Monitor fraud detection and security metrics.
          </Typography>
        </Box>
        {/* {chartData && <RiskTab sampleAnalytics={chartData} />} */}
      </TabPanel>
    </Box>
  );
};

const OverviewTab: React.FC<{ sampleAnalytics: any }> = ({ sampleAnalytics }) => {

  // Custom color schemes for each chart - Updated with attractive colors
  const chartColors = {
    successLine: '#3ce759ff',  // Red for success
    settlementLine: '#2196F3',  // Blue for settlement
    successGateway: ['#9C27B0', '#4CAF50', '#FF9800', '#00BCD4', '#E91E63'],  // Purple (PayU), Light Green (Cashfree), Orange, Cyan, Pink
    failedGateway: ['#F44336', '#FF5722', '#D32F2F', '#C62828', '#B71C1C']  // Red shades for failed transactions
  };

  if (!sampleAnalytics) {
    return null;
  }

  // line charts
  const success = mapLineData(sampleAnalytics.total_success_transactions || [], 'total_transactions');
  const settlement = mapLineData(sampleAnalytics.total_settlement_amount || [], 'total_settlement_amount');

  // gateway-wise
  const successGw = mapGatewaySeries(sampleAnalytics.total_success_transactions_gateway_wise || []);
  const failedGw = mapGatewaySeries(sampleAnalytics.total_failed_transactions_gateway_wise || []);

  // Calculate summary metrics from API response - new structure with current, difference, trend
  const summary = sampleAnalytics.v1_summary || {};

  // Extract values from new API structure
  const successRateData = summary.success_rate || {};
  const successTransactionsData = summary.success_transactions || {};
  const overallTransactionsData = summary.overall_transactions || {};
  const processedAmountData = summary.processed_amount || {};
  const averageTicketSizeData = summary.average_ticket_size || {};
  const txnLatencyData = summary.txn_latency_minutes || {};

  return (
    <>
      <style>
        {`
          @media only screen and (max-width: 600px) {
            .css-1ml5fja-MuiGrid-root {
              width: 100% !important;
            }
          }
          .css-10r74af-MuiPaper-root {
                        background-color: transparent;
          
          }
        `}
      </style>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
          Overall Performance
        </Typography>
      </Box>

      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ boxShadow: 1, border: '1px solid #e5e7eb', borderRadius: 2, background:'#E8F5E9' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, color: 'text.secondary' }}
                >
                  Transaction Success Rate
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, font: '14px' }}>
                  {successRateData?.trend === 'no_change' ? <KeyboardArrowUpIcon sx={{ color: '#66BB6A' }}  /> : (successRateData?.trend === 'increase')? <KeyboardArrowUpIcon sx={{ color: 'rgb(76 175 80)' }} /> : <KeyboardArrowDownIcon sx={{ color: 'rgb(244 67 54)' }} />}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      color: successRateData?.trend === 'no_change' ? 'rgb(255 193 7)' : successRateData?.trend === 'decrease' ? 'rgb(244 67 54)' : successRateData?.trend === 'increase' ? 'rgb(76 175 80)' : '#000'
                    }}
                  >
                    {successRateData?.difference || 0}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="h4"
                sx={{ mt: 0.5, fontWeight: 'bold', color: 'text.primary' }}
              >
                {successRateData?.current || '0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ boxShadow: 1, border: '1px solid #e5e7eb', borderRadius: 2, background:'#E0F2F1' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, color: 'text.secondary' }}
                >
                  Success Transactions
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {successTransactionsData?.trend === 'no_change' ? <KeyboardArrowUpIcon sx={{ color: '#4DB6AC' }}  /> : (successTransactionsData?.trend === 'increase') ? <KeyboardArrowUpIcon sx={{ color: 'rgb(76 175 80)' }} /> : <KeyboardArrowDownIcon sx={{ color: 'rgb(244 67 54)' }} />}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      color: successTransactionsData?.trend === 'no_change' ? 'rgb(255 193 7)' : successTransactionsData?.trend === 'decrease' ? 'rgb(244 67 54)' : successTransactionsData?.trend === 'increase' ? 'rgb(76 175 80)' : '#000'
                    }}
                  >
                    {successTransactionsData?.difference || 0}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="h4"
                sx={{ mt: 0.5, fontWeight: 'bold', color: 'text.primary' }}
              >
                {successTransactionsData?.current || '0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ boxShadow: 1, border: '1px solid #e5e7eb', borderRadius: 2, background:'#E3F2FD' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, color: 'text.secondary' }}
                >
                  Overall Transactions
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {overallTransactionsData?.trend === 'no_change' ? <KeyboardArrowUpIcon sx={{ color: '#64B5F6' }}  /> : (overallTransactionsData?.trend === 'increase') ? <KeyboardArrowUpIcon sx={{ color: 'rgb(76 175 80)' }} /> : <KeyboardArrowDownIcon sx={{ color: 'rgb(244 67 54)' }} />}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      color: overallTransactionsData?.trend === 'no_change' ? 'rgb(255 193 7)' : overallTransactionsData?.trend === 'decrease' ? 'rgb(244 67 54)' : overallTransactionsData?.trend === 'increase' ? 'rgb(76 175 80)' : '#000'
                    }}
                  >
                    {overallTransactionsData?.difference || 0}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="h4"
                sx={{ mt: 0.5, fontWeight: 'bold', color: 'text.primary' }}
              >
                {overallTransactionsData?.current || '0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ boxShadow: 1, border: '1px solid #e5e7eb', borderRadius: 2,  background:'#F3E5F5' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, color: 'text.secondary' }}
                >
                  Processed Amount
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {processedAmountData?.trend === 'no_change' ? <KeyboardArrowUpIcon sx={{ color: '#BA68C8' }}  /> : (processedAmountData?.trend === 'increase') ? <KeyboardArrowUpIcon sx={{ color: 'rgb(76 175 80)' }} /> : <KeyboardArrowDownIcon sx={{ color: 'rgb(244 67 54)' }} />}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      color: processedAmountData?.trend === 'no_change' ? 'rgb(255 193 7)' : processedAmountData?.trend === 'decrease' ? 'rgb(244 67 54)' : processedAmountData?.trend === 'increase' ? 'rgb(76 175 80)' : '#000'
                    }}
                  >
                    {processedAmountData?.difference || 0}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="h4"
                sx={{ mt: 0.5, fontWeight: 'bold', color: 'text.primary' }}
              >
                {processedAmountData?.current || '0'}
              </Typography>
            </CardContent>
          </Card>

        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ boxShadow: 1, border: '1px solid #e5e7eb', borderRadius: 2,  background:'#FFF3E0' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, color: 'text.secondary' }}
                >
                  Average Ticket Size
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {averageTicketSizeData?.trend === 'no_change' ? <KeyboardArrowUpIcon sx={{ color: '#FFB74D' }} /> : (averageTicketSizeData?.trend === 'increase') ? <KeyboardArrowUpIcon sx={{ color: 'rgb(76 175 80)' }} /> : <KeyboardArrowDownIcon sx={{ color: 'rgb(244 67 54)' }} />}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      color: averageTicketSizeData?.trend === 'no_change' ? 'rgb(255 193 7)' : averageTicketSizeData?.trend === 'decrease' ? 'rgb(244 67 54)' : averageTicketSizeData?.trend === 'increase' ? 'rgb(76 175 80)' : '#000'
                    }}
                  >
                    {averageTicketSizeData?.difference || 0}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="h4"
                sx={{ mt: 0.5, fontWeight: 'bold', color: 'text.primary' }}
              >
                {averageTicketSizeData?.current || '0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Row 2: Last card */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ boxShadow: 1, border: '1px solid #e5e7eb', borderRadius: 2,  background:'#FDECEA' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, color: 'text.secondary' }}
                >
                  Txn Latency
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>


                  {txnLatencyData?.trend === 'no_change' ? <KeyboardArrowUpIcon sx={{ color: '#E57373' }} /> : (txnLatencyData?.trend === 'increase' )  ? <KeyboardArrowUpIcon sx={{ color: 'rgb(76 175 80)' }} /> : <KeyboardArrowDownIcon sx={{ color: 'rgb(244 67 54)' }} />}


                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      color: txnLatencyData?.trend === 'no_change' ? 'rgb(255 193 7)' : txnLatencyData?.trend === 'decrease' ? 'rgb(244 67 54)' : txnLatencyData?.trend === 'increase' ? 'rgb(76 175 80)' : '#000'
                    }}
                  >
                    {txnLatencyData?.difference || 0}
                  </Typography>



                </Box>
              </Box>
              <Typography
                variant="h4"
                sx={{ mt: 0.5, fontWeight: 'bold', color: 'text.primary' }}
              >
                {txnLatencyData?.current || '0'}
              </Typography>
            </CardContent>
          </Card>

        </Grid>
        {/* Charts */}
        <Grid size={12}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, minHeight: 340, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Total Successful Transactions (Datewise)
            </Typography>
            <ResponsiveChart
              dataLabels={success.labels}
              dataSeries={[{
                data: success.data,
                label: 'Successful Transactions',
                color: chartColors.successLine
              }]}
              chartType="line"
            />
          </Paper>
        </Grid>

        <Grid size={12}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, minHeight: 340, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Total Settlement Amount (Datewise)
            </Typography>
            <ResponsiveChart
              dataLabels={settlement.labels}
              dataSeries={[{
                data: settlement.data,
                label: 'Settlement Amount',
                color: chartColors.settlementLine
              }]}
              chartType="line"
            />
          </Paper>
        </Grid>

        <Grid size={12}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, minHeight: 340, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Successful Transactions Gateway-wise (Datewise)
            </Typography>
            <ResponsiveChart
              dataLabels={successGw.labels}
              dataSeries={successGw.series.map((series, index) => ({
                ...series,
                color: chartColors.successGateway[index % chartColors.successGateway.length]
              }))}
              chartType="bar"
            />
          </Paper>
        </Grid>

        <Grid size={12}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, minHeight: 340, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Failed Transactions Gateway-wise (Datewise)
            </Typography>
            <ResponsiveChart
              dataLabels={failedGw.labels}
              dataSeries={failedGw.series.map((series, index) => ({
                ...series,
                color: chartColors.failedGateway[index % chartColors.failedGateway.length]
              }))}
              chartType="bar"
            />
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

const VolumeTab: React.FC<{ sampleAnalytics: any }> = ({ sampleAnalytics }) => {
  const success = mapLineData(sampleAnalytics.total_success_transactions || [], 'total_transactions');
  const settlement = mapLineData(sampleAnalytics.total_settlement_amount || [], 'total_settlement_amount');
  const successGw = mapGatewaySeries(sampleAnalytics.total_success_transactions_gateway_wise || []);

  const chartColors = {
    successLine: '#E74C3C',
    settlementLine: '#2196F3',
    successGateway: ['#9C27B0', '#4CAF50', '#FF9800', '#00BCD4', '#E91E63'],
  };

  return (
    <Grid container spacing={4}>
      <Grid size={12}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2, minHeight: 340 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Total Successful Transactions (Datewise)
          </Typography>
          <ResponsiveChart
            dataLabels={success.labels}
            dataSeries={[{
              data: success.data,
              label: 'Successful Transactions',
              color: chartColors.successLine
            }]}
            chartType="line"
          />
        </Paper>
      </Grid>
      <Grid size={12}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2, minHeight: 340 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Total Settlement Amount (Datewise)
          </Typography>
          <ResponsiveChart
            dataLabels={settlement.labels}
            dataSeries={[{
              data: settlement.data,
              label: 'Settlement Amount',
              color: chartColors.settlementLine
            }]}
            chartType="line"
          />
        </Paper>
      </Grid>
      <Grid size={12}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2, minHeight: 340 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Successful Transactions Gateway-wise (Datewise)
          </Typography>
          <ResponsiveChart
            dataLabels={successGw.labels}
            dataSeries={successGw.series.map((series, index) => ({
              ...series,
              color: chartColors.successGateway[index % chartColors.successGateway.length]
            }))}
            chartType="bar"
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

const ConversionRateTab: React.FC<{ sampleAnalytics: any }> = ({ sampleAnalytics }) => {
  const summary = sampleAnalytics.v1_summary || {};
  const successRateData = summary.success_rate || {};
  const successTransactionsData = summary.success_transactions || {};
  const overallTransactionsData = summary.overall_transactions || {};

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card sx={{ boxShadow: 1, border: '1px solid #e5e7eb', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Success Rate
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
              {successRateData?.current || '0'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Trend: {successRateData?.trend || 'N/A'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card sx={{ boxShadow: 1, border: '1px solid #e5e7eb', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Conversion Ratio
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#2196F3' }}>
              {successTransactionsData?.current && overallTransactionsData?.current 
                ? `${((parseInt(successTransactionsData.current) / parseInt(overallTransactionsData.current)) * 100).toFixed(1)}%`
                : '0%'
              }
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Success vs Total Transactions
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card sx={{ boxShadow: 1, border: '1px solid #e5e7eb', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Performance Score
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
              {successRateData?.current ? `${parseFloat(successRateData.current.replace('%', '')).toFixed(0)}/100` : '0/100'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Based on success rate
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const RiskTab: React.FC<{ sampleAnalytics: any }> = ({ sampleAnalytics }) => {
  const failedGw = mapGatewaySeries(sampleAnalytics.total_failed_transactions_gateway_wise || []);
  const summary = sampleAnalytics.v1_summary || {};
  const txnLatencyData = summary.txn_latency_minutes || {};
  const successRateData = summary.success_rate || {};

  const chartColors = {
    failedGateway: ['#F44336', '#FF5722', '#D32F2F', '#C62828', '#B71C1C']
  };

  // Calculate risk score based on success rate
  const calculateRiskScore = () => {
    if (!successRateData?.current) return 'High';
    const rate = parseFloat(successRateData.current.replace('%', ''));
    if (rate >= 95) return 'Low';
    if (rate >= 85) return 'Medium';
    return 'High';
  };

  const riskScore = calculateRiskScore();
  const riskColor = riskScore === 'Low' ? '#4CAF50' : riskScore === 'Medium' ? '#FF9800' : '#F44336';

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ boxShadow: 1, border: '1px solid #e5e7eb', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Risk Assessment
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: riskColor }}>
              {riskScore}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Based on transaction success rate
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ boxShadow: 1, border: '1px solid #e5e7eb', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Transaction Latency
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#9C27B0' }}>
              {txnLatencyData?.current || '0'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Average processing time
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={12}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2, minHeight: 340 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Failed Transactions Gateway-wise (Risk Analysis)
          </Typography>
          <ResponsiveChart
            dataLabels={failedGw.labels}
            dataSeries={failedGw.series.map((series, index) => ({
              ...series,
              color: chartColors.failedGateway[index % chartColors.failedGateway.length]
            }))}
            chartType="bar"
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DashboardPage;